// ─── widgets/text.js ──────────────────────────────────────────────────────────
// Creates cleverstory.v2.widgets.text widget objects.

import shortid from 'shortid';
import {
    cs, getAbsoluteRect,
    getFontFamily, getFontSize, getFontWeight, getLineHeight,
    getLetterSpacing, getTextTransform, getColor, getAlignment, getOverflow,
    safeAlign, getTextTypeAndLevel, isVisible,
} from '../utils/dom.js';
import { resolveFontFamily, normalizeFontFamily } from '../utils/fonts.js';
import { TEXT } from 'constants/widgetTypes.jsx';

// ── Style extractor ───────────────────────────────────────────────────────────
function extractStylesFromEl(el) {
    return {
        text:          el.textContent.trim(),
        fontFamily:    getFontFamily(el),
        fontSize:      getFontSize(el),
        fontWeight:    getFontWeight(el),
        lineHeight:    getLineHeight(el),
        letterSpacing: getLetterSpacing(el),
        textTransform: getTextTransform(el),
        color:         getColor(el),
        textAlign:     getAlignment(el),
        overflow:      getOverflow(el),
    };
}

// ── List HTML builder ─────────────────────────────────────────────────────────
function buildListHtml(listEl, fontFamilyMap, fontIdMap) {
    const p = extractStylesFromEl(listEl);
    const { resolvedFamily } = resolveFontFamily(p.fontFamily, fontFamilyMap, fontIdMap);
    let items = '';

    listEl.querySelectorAll('li').forEach((li) => {
        if (!isVisible(li)) return;
        const lp = extractStylesFromEl(li);
        if (!lp.text) return;
        const s = [
            `font-size:${lp.fontSize ?? p.fontSize ?? 16}px`,
            `color:${lp.color ?? p.color ?? 'inherit'}`,
            `font-weight:${lp.fontWeight ?? p.fontWeight ?? 'normal'}`,
            `text-align:${lp.textAlign ?? p.textAlign ?? 'left'}`,
            `overflow:${lp.overflow ?? p.overflow ?? 'visible'}`,
        ].join(';') + ';';
        items += `<li style="${s}">${lp.text}</li>`;
    });

    const ff = normalizeFontFamily(resolvedFamily || p.fontFamily);
    const ws = [
        `font-family:${ff}`,
        `font-size:${p.fontSize ?? 16}px`,
        `color:${p.color ?? 'inherit'}`,
        `font-weight:${p.fontWeight ?? 'normal'}`,
        `text-align:${p.textAlign ?? 'left'}`,
        `letter-spacing:${p.letterSpacing ?? 0}px`,
    ].join(';') + ';';

    return `<${listEl.tagName.toLowerCase()} style="${ws}">${items}</${listEl.tagName.toLowerCase()}>`;
}

// ── Inline HTML builder ───────────────────────────────────────────────────────
function buildTextHtml(rawText, type, level, fontFamily, fontSize, color, fontWeight, overflow, letterSpacing, textTransform) {
    const ff = normalizeFontFamily(fontFamily);
    const style = [
        `font-size:${fontSize ?? 16}px`,
        `font-family:${ff}`,
        `color:${color ?? 'inherit'}`,
        `font-weight:${fontWeight ?? 'normal'}`,
        `overflow:${overflow ?? 'visible'}`,
        `letter-spacing:${letterSpacing ?? 0}px`,
        `text-transform:${textTransform ?? 'none'}`,
    ].join(';') + ';';

    if (type === 'heading') return `<h${level} style="${style}">${rawText}</h${level}>`;
    if (type === 'list')    return rawText;
    return `<p style="${style}">${rawText}</p>`;
}

// ── Public creator ────────────────────────────────────────────────────────────
/**
 * @param {{ el, mobileEl, parentUid, offsetRect, mobileOffsetRect, orderRef, fontFamilyMap, fontIdMap }}
 */
export function createTextWidgetObj({ el, mobileEl, parentUid, offsetRect, mobileOffsetRect, orderRef, fontFamilyMap, fontIdMap }) {
    const styles = extractStylesFromEl(el);
    const rect   = getAbsoluteRect(el);
    const mRect  = mobileEl ? getAbsoluteRect(mobileEl) : null;
    const tag    = el.tagName.toLowerCase();
    const isList = tag === 'ul' || tag === 'ol';

    let type, level, rawContent;
    if (isList) {
        type = 'list'; level = null;
        rawContent = buildListHtml(el, fontFamilyMap, fontIdMap);
    } else {
        const d = getTextTypeAndLevel(styles.fontSize);
        type = d.type; level = d.level;
        rawContent = styles.text;
    }

    const { resolvedFamily, fontIds } = resolveFontFamily(styles.fontFamily, fontFamilyMap, fontIdMap);
    const rff      = resolvedFamily || styles.fontFamily;
    const finalHtml = buildTextHtml(rawContent, type, level, rff, styles.fontSize, styles.color, styles.fontWeight, styles.overflow, styles.letterSpacing, styles.textTransform);

    // Desktop position (relative to parent section/column rect)
    const x = Math.round(rect.left - (offsetRect.left || 0));
    const y = Math.round(rect.top  - (offsetRect.top  || 0));
    const w = Math.round(rect.width + styles.fontSize * 0.5 + 4) + 'px';
    const h = Math.round(rect.height) + 'px';

    // Mobile position
    const mOff = mobileOffsetRect || offsetRect;
    const mx   = mRect ? Math.round(mRect.left - (mOff.left || 0)) : Math.min(x, 20);
    const my   = mRect ? Math.round(mRect.top  - (mOff.top  || 0)) : y;
    const mw   = mRect ? Math.round(mRect.width + 6) + 'px' : Math.min(parseInt(w), 335) + 'px';
    const mh   = mRect ? Math.round(mRect.height) + 'px' : h;

    const ms                                   = mobileEl ? extractStylesFromEl(mobileEl) : styles;
    const { resolvedFamily: mrf, fontIds: mfi } = resolveFontFamily(ms.fontFamily, fontFamilyMap, fontIdMap);
    const mfs                                   = ms.fontSize.toString();
    const allFontIds                            = [...new Set([...fontIds, ...mfi])];
    const name = type === 'heading' ? `Heading ${level}` : type === 'list' ? 'List' : 'Paragraph';
    const lineHeightPx = parseFloat(styles.lineHeight);
    const fontSizePx = parseFloat(styles.fontSize);
    const mobileLineHeightPx = parseFloat(ms.lineHeight);
    const mobileFontSizePx = parseFloat(ms.fontSize);
    const lineHeightUnitlessRaw = lineHeightPx / fontSizePx;
    const mobileLineHeightUnitlessRaw = mobileLineHeightPx / mobileFontSizePx;
    const lineHeightUnitless = 
        lineHeightUnitlessRaw > 0 && lineHeightUnitlessRaw < 1
        ? 1.2
        : lineHeightUnitlessRaw;
    const mobileLineHeightUnitless =
        mobileLineHeightUnitlessRaw > 0 && mobileLineHeightUnitlessRaw < 1
        ? 1.2
        : mobileLineHeightUnitlessRaw;
    
    return {
        uid:           `widget_${shortid.generate()}`,
        type:          TEXT,
        name,
        parent:        parentUid,
        order:         orderRef.value++,
        systemDefined: false,
        fonts:         allFontIds.length > 0 ? allFontIds : null,
        parameter:     null,
        actions:       [],
        properties: {
            desktop: {
                visible: true,
                layout: {
                    transform: { translate: `${x}px,${y}px`, rotate: '0deg', scale: '1,1' },
                    width: w, height: h,
                },
                fontSize:      styles.fontSize.toString(),
                lineSpacing:   lineHeightUnitless,
                letterSpacing: 0,
                color:         null,
                alignment:     safeAlign(styles.textAlign),
                animation:     null,
                fontFamily:    rff || null,
            },
            mobile: {
                visible: true,
                layout: {
                    transform: { translate: `${mx}px,${my}px`, rotate: '0deg', scale: '1,1' },
                    width: mw, height: mh,
                },
                fontSize:      mfs,
                lineSpacing:   mobileLineHeightUnitless,
                letterSpacing: 0,
                color:         null,
                alignment:     safeAlign(ms.textAlign),
                animation:     null,
                fontFamily:    mrf || ms.fontFamily || null,
            },
        },
        content: finalHtml,
    };
}